"use client";

import { useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

interface InvisibleChar {
  id: string;
  codePoint: string;
  char: string;
}

export const INVISIBLE_CHARS: InvisibleChar[] = [
  { id: "zwsp", codePoint: "U+200B", char: "​" },
  { id: "zwnj", codePoint: "U+200C", char: "‌" },
  { id: "word-joiner", codePoint: "U+2060", char: "⁠" },
  { id: "hangul-filler", codePoint: "U+3164", char: "ㅤ" },
  { id: "nbsp", codePoint: "U+00A0", char: " " },
];

const DEFAULT_QUANTITY = 5;
const MAX_QUANTITY = 1000;

/** English strings. A translated content file overrides these through `ui`. */
export const INVISIBLE_CHARACTER_STRINGS = {
  names: {
    zwsp: "Zero-width space",
    zwnj: "Zero-width non-joiner",
    "word-joiner": "Word joiner",
    "hangul-filler": "Hangul filler",
    nbsp: "No-break space",
  },
  descriptions: {
    zwsp: "The most popular invisible character. Zero width, no glyph — the go-to choice for blank messages and hidden separators.",
    zwnj: "Prevents adjacent characters from joining in cursive scripts. Renders as nothing in Latin text.",
    "word-joiner":
      "An invisible character that also prevents line breaks at its position. Survives some filters that strip zero-width spaces.",
    "hangul-filler":
      "Has visible width but no glyph, so it looks like a blank block of space. A favorite for invisible usernames.",
    nbsp: "Looks like a normal space and prevents line breaks, but JavaScript trim() and Python strip() remove it, so it is a weak choice for a blank name or message.",
  },
  copied: "Copied!",
  copy: "Copy",
  copyMultipleInvisibleCharacters: "Copy multiple invisible characters",
  quantity: "Quantity",
  character: "Character",
  copyQuantity:
    "one:Copy {count} invisible character|other:Copy {count} invisible characters",
  testAreaLabel: "Test area — paste here to verify your copy worked",
  testAreaPlaceholder: "Paste the invisible character here",
  testAreaCount: "Characters in test area: {count}",
  testAreaCountGrew:
    "Characters in test area: {count} — the count went up, so the invisible character is really there.",
  whatPeopleUseInvisibleCharactersFor:
    "What people use invisible characters for",
  useDiscordWhatsapp: "Sending empty messages on Discord and WhatsApp",
  useInstagramBio: "Making a blank Instagram name or bio line",
  useGamerTag: "Blank Fortnite or gamer-tag style usernames",
  useHiddenSeparators: "Adding hidden separators or padding inside text",
  honestCaveat:
    "Honest caveat: some platforms strip zero-width and formatting characters from usernames and messages to prevent abuse, so an invisible character that works in one app may be removed by another. Invisible characters can also confuse screen readers and text processing, so use them sparingly.",
} as const;

export default function InvisibleCharacterWidget() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  const [quantityCharId, setQuantityCharId] = useState("zwsp");
  const [testValue, setTestValue] = useState("");

  const strings = useToolUi(INVISIBLE_CHARACTER_STRINGS);
  const locale = useToolLocale();

  const testCount = [...testValue].length;
  const quantityChar =
    INVISIBLE_CHARS.find((c) => c.id === quantityCharId) ?? INVISIBLE_CHARS[0];

  function handleCopy(id: string, value: string) {
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  function handleQuantityChange(raw: string) {
    const parsed = parseIntegerInput(raw);
    if (parsed === null) {
      setQuantity(1);
      return;
    }
    setQuantity(Math.min(Math.max(parsed, 1), MAX_QUANTITY));
  }

  return (
    <>
      {/* Character cards */}
      <section className="space-y-4">
        {INVISIBLE_CHARS.map((c) => (
          <div
            className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-card p-5"
            key={c.id}
          >
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-medium text-foreground">
                {strings.names[c.id as keyof typeof strings.names]}{" "}
                <span className="font-mono text-muted-foreground text-xs">
                  {c.codePoint}
                </span>
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {
                  strings.descriptions[
                    c.id as keyof typeof strings.descriptions
                  ]
                }
              </p>
            </div>
            <button
              className="rounded-lg bg-primary px-4 py-1.5 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
              onClick={() => handleCopy(c.id, c.char)}
              type="button"
            >
              {copiedId === c.id ? strings.copied : strings.copy}
            </button>
          </div>
        ))}
      </section>

      {/* Quantity copy */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-5">
        <h2 className="font-semibold text-base text-foreground">
          {strings.copyMultipleInvisibleCharacters}
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="invisible-quantity"
            >
              {strings.quantity}
            </label>
            <input
              className="w-24 rounded-lg border border-border bg-background p-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="invisible-quantity"
              max={MAX_QUANTITY}
              min={1}
              onChange={(e) => handleQuantityChange(e.target.value)}
              type="number"
              value={quantity}
            />
          </div>
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="invisible-quantity-char"
            >
              {strings.character}
            </label>
            <select
              className="rounded-lg border border-border bg-background p-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="invisible-quantity-char"
              onChange={(e) => setQuantityCharId(e.target.value)}
              value={quantityCharId}
            >
              {INVISIBLE_CHARS.map((c) => (
                <option key={c.id} value={c.id}>
                  {strings.names[c.id as keyof typeof strings.names]} (
                  {c.codePoint})
                </option>
              ))}
            </select>
          </div>
          <button
            className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
            onClick={() =>
              handleCopy("quantity", quantityChar.char.repeat(quantity))
            }
            type="button"
          >
            {copiedId === "quantity"
              ? strings.copied
              : tPlural(strings.copyQuantity, locale, quantity)}
          </button>
        </div>
      </section>

      {/* Test area */}
      <section className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="invisible-test-area"
        >
          {strings.testAreaLabel}
        </label>
        <textarea
          className="min-h-[100px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="invisible-test-area"
          onChange={(e) => setTestValue(e.target.value)}
          placeholder={strings.testAreaPlaceholder}
          value={testValue}
        />
        <p className="text-muted-foreground text-sm">
          {renderSlots(
            testCount > 0 ? strings.testAreaCountGrew : strings.testAreaCount,
            {
              count: (
                <span className="font-semibold text-foreground">
                  {testCount}
                </span>
              ),
            }
          )}
        </p>
      </section>

      {/* Uses */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-5">
        <h2 className="font-semibold text-base text-foreground">
          {strings.whatPeopleUseInvisibleCharactersFor}
        </h2>
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm leading-relaxed">
          <li>{strings.useDiscordWhatsapp}</li>
          <li>{strings.useInstagramBio}</li>
          <li>{strings.useGamerTag}</li>
          <li>{strings.useHiddenSeparators}</li>
        </ul>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.honestCaveat}
        </p>
      </section>
    </>
  );
}
